import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  desescalar,
  dividirArredondando,
  escalar,
} from './aritmetica.ts';
import { ufSchema } from './cidades.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  percentualSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import { textoOpcional } from './endereco.ts';
import {
  codigoTributacaoMunicipalSchema,
  codigoTributacaoNacionalSchema,
  nbsSchema,
  tributacaoIssSchema,
  type TributacaoIss,
} from './fiscal.ts';
import { motorNfseSchema } from './motores-nfse.ts';
import { saldosAFaturarSchema } from './saldo-a-faturar.ts';
import type { CampoDeRetencao } from './operacoes-fiscais.ts';

/**
 * Nota fiscal de serviço emitida (`.spec/010`, módulo Faturamento;
 * `.spec/015`, decisões 90 e 100). Tela `FAT·01` — era `CTR·06` até a nota
 * sair do módulo de Contratos e Obras.
 *
 * É o documento com que a construtora **cobra** o serviço que prestou, e o
 * primeiro do sistema que nasce aqui e vale fora: o que a nota de compra faz
 * com o papel do fornecedor, esta faz com o papel da própria empresa.
 *
 * ## O que a nota é, e o que não é
 *
 * - **Um serviço por nota**, sem itens. É o desenho da NFS-e nacional — uma
 *   declaração, um código de tributação — e não economia: a nota com três
 *   serviços seriam três notas na prefeitura.
 * - **Uma obra por nota, quando há obra.** O grupo "obra" da NFS-e é um só, e o
 *   ISS da construção é devido no município dela. A obra é **opcional**: projeto,
 *   consultoria e locação saem sem obra nenhuma.
 * - **O título a receber nasce da nota**, e não o contrário. Emitida, ela gera
 *   as parcelas pela condição de pagamento, pelo **valor líquido** — o que o
 *   tomador reteve na fonte ele não paga à empresa. Cancelada, apaga o que
 *   gerou.
 * - **A nota não transmite.** Quem transmite é o motor configurado na conta
 *   (`motores-nfse.ts`); a nota não sabe qual é, e é isso que deixa trocar de
 *   fornecedor sem tocar neste arquivo.
 *
 * ## A operação fiscal manda no financeiro e nos impostos
 *
 * Como na nota de compra: o payload traz o **id** da operação, não as marcas
 * dela. É a operação que diz se a nota gera contas a receber, como o ISS incide
 * e quais retenções a nota aceita. O servidor copia para a nota o que leu
 * (`tributacaoIss`, `issRetido`) no momento de gravar — editar a operação
 * amanhã não reescreve a nota de ontem.
 */

/* -------------------------------------------------------------------------- */
/* Situação                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * As cinco situações.
 *
 * `PROCESSANDO` e `REJEITADA` existem para o motor **assíncrono** — o que
 * recebe a nota, devolve um protocolo e avisa depois. O motor Manual nunca
 * passa por elas: quem digita o número já tem a nota autorizada na mão. Estão
 * na lista desde já porque a porta do motor as devolve, e acrescentá-las com a
 * primeira API seria uma migração no meio da integração.
 *
 * Não há `ATIVO`: nota é documento, e documento tem situação.
 */
export const SITUACOES_NOTA_SERVICO = [
  'RASCUNHO',
  'PROCESSANDO',
  'EMITIDA',
  'REJEITADA',
  'CANCELADA',
] as const;
export const situacaoNotaServicoSchema = z.enum(SITUACOES_NOTA_SERVICO);
export type SituacaoNotaServico = z.infer<typeof situacaoNotaServicoSchema>;

export const ROTULO_SITUACAO_NOTA_SERVICO: Readonly<Record<SituacaoNotaServico, string>> = {
  RASCUNHO: 'Rascunho',
  PROCESSANDO: 'Em processamento',
  EMITIDA: 'Emitida',
  REJEITADA: 'Rejeitada',
  CANCELADA: 'Cancelada',
};

export const DICA_SITUACAO_NOTA_SERVICO: Readonly<Record<SituacaoNotaServico, string>> = {
  RASCUNHO: 'Ainda não foi emitida. Pode ser alterada e excluída.',
  PROCESSANDO: 'Foi enviada e aguarda a resposta da prefeitura.',
  EMITIDA: 'Autorizada. Só se desfaz cancelando.',
  REJEITADA: 'A prefeitura recusou. Corrija o que ela apontou e emita de novo.',
  CANCELADA: 'Cancelada depois de emitida. Fica no histórico e não volta.',
};

/**
 * A nota aceita alteração e exclusão?
 *
 * **Só a que nunca foi autorizada.** Rascunho é rascunho; a rejeitada voltou da
 * prefeitura sem valer nada, e corrigi-la é justamente o que se espera. A
 * emitida é fato fiscal — o que se corrige nela é outra nota —, e a cancelada
 * é histórico.
 *
 * `PROCESSANDO` fica de fora de propósito: alterar a nota enquanto o motor a
 * transmite faria a resposta chegar para um documento que já é outro.
 */
export function notaServicoEditavel(situacao: SituacaoNotaServico): boolean {
  return situacao === 'RASCUNHO' || situacao === 'REJEITADA';
}

/** Emite-se o que se edita: a mesma lista, com nome próprio para a tela ler. */
export function emissaoPermitida(situacao: SituacaoNotaServico): boolean {
  return notaServicoEditavel(situacao);
}

/** Só a emitida se cancela. A que está em processamento ainda não é nota. */
export function cancelamentoPermitido(situacao: SituacaoNotaServico): boolean {
  return situacao === 'EMITIDA';
}

/* -------------------------------------------------------------------------- */
/* Valores                                                                    */
/* -------------------------------------------------------------------------- */

const naoNegativo = (nome: string) =>
  dinheiroSchema
    .refine((v) => !v.startsWith('-'), `${nome} não pode ser negativo.`)
    .prefault('0.00');

/** Os cinco valores retidos que se digitam. O ISS retido é calculado. */
export const CAMPOS_DE_VALOR_RETIDO = [
  'valorIrrfRetido',
  'valorInssRetido',
  'valorCsllRetido',
  'valorPisRetido',
  'valorCofinsRetido',
] as const;

export type CampoDeValorRetido = (typeof CAMPOS_DE_VALOR_RETIDO)[number];

/** Qual marca da operação fiscal libera cada valor retido. */
export const MARCA_DO_VALOR_RETIDO: Readonly<
  Record<CampoDeValorRetido, Exclude<CampoDeRetencao, 'retemIss'>>
> = {
  valorIrrfRetido: 'retemIrrf',
  valorInssRetido: 'retemInss',
  valorCsllRetido: 'retemCsll',
  valorPisRetido: 'retemPis',
  valorCofinsRetido: 'retemCofins',
};

export const ROTULO_VALOR_RETIDO: Readonly<Record<CampoDeValorRetido, string>> = {
  valorIrrfRetido: 'IRRF retido',
  valorInssRetido: 'INSS retido',
  valorCsllRetido: 'CSLL retido',
  valorPisRetido: 'PIS retido',
  valorCofinsRetido: 'COFINS retido',
};

/** O que a nota precisa saber da operação fiscal para fazer a conta. */
export interface ImpostosDaOperacao {
  tributacaoIss: TributacaoIss;
  retemIss: boolean;
}

export interface ValoresDaNotaServico {
  /** Serviço menos deduções e desconto incondicionado. */
  valorBaseIss: string;
  /** Base × alíquota. Zero quando o ISS não incide. */
  valorIss: string;
  /** O ISS, quando quem o recolhe é o tomador. Zero nos demais casos. */
  valorIssRetido: string;
  /** Soma de tudo o que o tomador reteve — ISS retido e os cinco digitados. */
  valorRetido: string;
  /** O que o tomador paga: serviço, menos desconto incondicionado, menos o retido. */
  valorLiquido: string;
}

/**
 * A conta da nota — **uma só**, usada pela tela enquanto se digita e pelo
 * servidor ao gravar.
 *
 * Duas implementações discordariam num arredondamento, e o líquido mudaria ao
 * salvar: é o defeito que `valoresDaProposta`, na cotação, existe para evitar.
 * `BigInt` sobre centavos, sem `Number` — `decimal.js` é de `@construtora/domain`,
 * que a tela não carrega.
 *
 * ## O que desconta e o que não
 *
 * - **Dedução** (material aplicado, subempreitada já tributada) reduz a **base
 *   do ISS**, e só ela. O tomador paga o serviço inteiro.
 * - **Desconto incondicionado** reduz a base **e** o que o tomador paga.
 * - **Retenção** não muda a base: o imposto incide igual, e o que muda é quem
 *   recolhe. Por isso ela sai do líquido e não entra na conta do ISS.
 *
 * A base nunca fica negativa aqui — quem recusa a dedução maior que o serviço é
 * o schema (`valoresCoerentes`); o piso em zero é rede para a tela, que chama
 * esta função com o formulário pela metade.
 */
export function valoresDaNotaServico(
  entrada: {
    valorServico: string;
    valorDeducoes: string;
    valorDescontoIncondicionado: string;
    aliquotaIss: string;
  } & Readonly<Record<CampoDeValorRetido, string>>,
  impostos: ImpostosDaOperacao,
): ValoresDaNotaServico {
  const centavos = (valor: string) => escalar(valor || '0', CASAS_DINHEIRO);

  const servico = centavos(entrada.valorServico);
  const desconto = centavos(entrada.valorDescontoIncondicionado);
  const baseBruta = servico - centavos(entrada.valorDeducoes) - desconto;
  const base = baseBruta < 0n ? 0n : baseBruta;

  const aliquota = escalar(entrada.aliquotaIss || '0', CASAS_PERCENTUAL);
  const iss =
    impostos.tributacaoIss === 'TRIBUTAVEL'
      ? dividirArredondando(base * aliquota, 100n * 10n ** BigInt(CASAS_PERCENTUAL))
      : 0n;
  const issRetido = impostos.retemIss ? iss : 0n;

  const digitado = CAMPOS_DE_VALOR_RETIDO.reduce(
    (soma, campo) => soma + centavos(entrada[campo]),
    0n,
  );
  const retido = issRetido + digitado;

  return {
    valorBaseIss: desescalar(base, CASAS_DINHEIRO),
    valorIss: desescalar(iss, CASAS_DINHEIRO),
    valorIssRetido: desescalar(issRetido, CASAS_DINHEIRO),
    valorRetido: desescalar(retido, CASAS_DINHEIRO),
    valorLiquido: desescalar(servico - desconto - retido, CASAS_DINHEIRO),
  };
}

/**
 * Os valores retidos que a operação fiscal **não** autoriza.
 *
 * A operação diz quais retenções se aplicam; valor digitado numa que ela não
 * marcou é imposto retido sem natureza que o explique. A tela esconde o campo,
 * e esta função é o que o servidor usa para recusar — e para dizer qual.
 */
export function retencoesForaDaOperacao(
  entrada: Readonly<Record<CampoDeValorRetido, string>>,
  operacao: Readonly<Record<Exclude<CampoDeRetencao, 'retemIss'>, boolean>>,
): CampoDeValorRetido[] {
  return CAMPOS_DE_VALOR_RETIDO.filter(
    (campo) => Number(entrada[campo] || '0') > 0 && !operacao[MARCA_DO_VALOR_RETIDO[campo]],
  );
}

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/** Teto da descrição do serviço — o da NFS-e nacional. */
export const TAMANHO_MAXIMO_DESCRICAO_SERVICO = 2000;
export const TAMANHO_MAXIMO_INFORMACOES_COMPLEMENTARES = 2000;
export const TAMANHO_MAXIMO_OBSERVACAO_NOTA_SERVICO = 500;

/** O mesmo formato de `dinheiroSchema`. Ver a guarda em `valoresCoerentes`. */
const DINHEIRO_BEM_FORMADO = /^-?\d{1,16}(\.\d{1,2})?$/;

function valoresCoerentes(
  dados: { valorServico: string; valorDeducoes: string; valorDescontoIncondicionado: string },
  ctx: z.RefinementCtx,
): void {
  /*
    O `superRefine` roda **mesmo com campo já recusado**: o Zod junta os erros
    do objeto antes de devolver. Com `valorServico: "12,50"` o campo já levou a
    mensagem de formato, e a conta abaixo chamaria `BigInt("12,5000")`, que não
    devolve erro — lança. O payload montado fora da tela viraria 500 em vez de
    422. Sai cedo: o que está malformado já foi dito, e comparar valores que não
    são números não acrescenta nada.
  */
  const valores = [dados.valorServico, dados.valorDeducoes, dados.valorDescontoIncondicionado];
  if (!valores.every((valor) => DINHEIRO_BEM_FORMADO.test(valor))) return;

  const servico = escalar(dados.valorServico, CASAS_DINHEIRO);
  const abatido =
    escalar(dados.valorDeducoes, CASAS_DINHEIRO) +
    escalar(dados.valorDescontoIncondicionado, CASAS_DINHEIRO);

  if (abatido <= servico) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['valorDeducoes'],
    message: 'Deduções e desconto somados passam do valor do serviço.',
  });
}

/**
 * Payload de criação e de alteração.
 *
 * ## O que não está aqui, e por quê
 *
 * - `numero` — vem do contador da conta (`NOTA_SERVICO`), dentro da transação.
 * - `situacao` — muda por **ato** (emitir, cancelar), nunca por campo. Um
 *   `PUT {situacao: 'EMITIDA'}` deixaria a nota se declarar emitida sem título
 *   e sem número da prefeitura.
 * - `motor`, `numeroNfse`, `chaveAcesso`, `dataEmissao` e os demais dados da
 *   autorização — quem os escreve é a emissão.
 * - `tributacaoIss` e `issRetido` — copiados da operação fiscal pelo servidor.
 * - `valorBaseIss`, `valorIss`, `valorIssRetido`, `valorLiquido` — calculados
 *   por `valoresDaNotaServico` e gravados. Aceitá-los deixaria a nota trazer um
 *   líquido que não fecha com os próprios valores.
 *
 * ## O que é opcional aqui e cobrado na emissão
 *
 * Conta contábil e condição de pagamento. A nota se rascunha no dia em que o
 * serviço é medido, e a classificação e o prazo podem vir depois — é a regra do
 * contrato e da obra: o que o ato exige, o ato confere. A emissão as cobra
 * **só** quando a operação fiscal gera financeiro.
 */
export const criarNotaServicoSchema = z
  .object({
    empresaId: uuidSchema,
    clienteId: uuidSchema,
    /** Operação de **saída**. Decide o financeiro e os impostos — ver o topo do arquivo. */
    operacaoFiscalId: uuidSchema,
    /** O serviço: insumo de tipo serviço marcado como prestado. */
    insumoId: uuidSchema,

    /** Opcional. Quando preenchida, é da empresa da nota e recebe a receita inteira. */
    obraId: uuidSchema.nullable().prefault(null),
    /** Opcional. Contrato de **recebimento** com o cliente da nota. */
    contratoId: uuidSchema.nullable().prefault(null),

    /**
     * O dia a que o serviço se refere. A NFS-e usa o mês dele como competência.
     *
     * Data de fato: não se fatura serviço que ainda não foi prestado.
     */
    dataCompetencia: dataDoFatoSchema,

    /**
     * Onde o serviço foi executado, quando não é o município da empresa.
     *
     * Na construção civil o ISS é devido no município da **obra** (LC 116,
     * art. 3º), e é este campo que o diz à prefeitura. Vazio é "no município do
     * prestador". A tela o sugere a partir da obra; o servidor não adivinha.
     */
    cidadePrestacaoId: uuidSchema.nullable().prefault(null),

    descricaoServico: z
      .string()
      .trim()
      .min(3, 'Descreva o serviço prestado.')
      .max(
        TAMANHO_MAXIMO_DESCRICAO_SERVICO,
        `A descrição tem no máximo ${TAMANHO_MAXIMO_DESCRICAO_SERVICO} caracteres.`,
      ),

    /*
      Os três códigos do serviço, **copiados** do insumo pela tela e gravados na
      nota. Cópia, e não junção: reclassificar o insumo amanhã não pode mudar o
      código com que a nota de ontem foi emitida.
    */
    codigoTributacaoNacional: codigoTributacaoNacionalSchema,
    nbs: nbsSchema,
    codigoTributacaoMunicipal: codigoTributacaoMunicipalSchema,

    valorServico: dinheiroSchema.refine(
      (v) => Number(v) > 0,
      'O valor do serviço deve ser maior que zero.',
    ),
    /** Material aplicado e subempreitada já tributada — reduzem só a base do ISS. */
    valorDeducoes: naoNegativo('A dedução'),
    valorDescontoIncondicionado: naoNegativo('O desconto'),

    aliquotaIss: percentualSchema
      .refine((v) => Number(v) <= 100, 'A alíquota não passa de 100%.')
      .prefault('0'),

    valorIrrfRetido: naoNegativo('O IRRF retido'),
    valorInssRetido: naoNegativo('O INSS retido'),
    valorCsllRetido: naoNegativo('A CSLL retida'),
    valorPisRetido: naoNegativo('O PIS retido'),
    valorCofinsRetido: naoNegativo('A COFINS retida'),

    /** Receita. Só conta folha do grupo de receitas — conferido na emissão. */
    contaContabilId: uuidSchema.nullable().prefault(null),
    /** Divide o líquido em parcelas, contadas da data de emissão. */
    condicaoPagamentoId: uuidSchema.nullable().prefault(null),

    /** Vai impresso no corpo da nota. */
    informacoesComplementares: textoOpcional(TAMANHO_MAXIMO_INFORMACOES_COMPLEMENTARES),
    /** Anotação interna. Não vai para a nota. */
    observacao: textoOpcional(TAMANHO_MAXIMO_OBSERVACAO_NOTA_SERVICO),
  })
  .superRefine(valoresCoerentes);

export type CriarNotaServico = z.infer<typeof criarNotaServicoSchema>;
export const atualizarNotaServicoSchema = criarNotaServicoSchema;
export type AtualizarNotaServico = z.infer<typeof atualizarNotaServicoSchema>;

/* -------------------------------------------------------------------------- */
/* Emissão e cancelamento                                                     */
/* -------------------------------------------------------------------------- */

export const TAMANHO_MAXIMO_NUMERO_NFSE = 20;
export const TAMANHO_MAXIMO_CHAVE_NFSE = 60;

/**
 * O que **a pessoa** informa sobre uma nota emitida fora do sistema.
 *
 * É o insumo do motor Manual, e de qualquer outro que não devolva a
 * autorização sozinho. Só o número e a data são exigidos: a chave de acesso e
 * o código de verificação existem no padrão nacional, mas a nota de prefeitura
 * que ainda não migrou não tem um dos dois.
 */
export const dadosInformadosDaEmissaoSchema = z.object({
  numeroNfse: z
    .string()
    .trim()
    .min(1, 'Informe o número da nota.')
    .max(
      TAMANHO_MAXIMO_NUMERO_NFSE,
      `O número tem no máximo ${TAMANHO_MAXIMO_NUMERO_NFSE} caracteres.`,
    ),
  /** Data de fato, e é dela que as parcelas contam. */
  dataEmissao: dataDoFatoSchema,
  chaveAcesso: textoOpcional(TAMANHO_MAXIMO_CHAVE_NFSE),
  codigoVerificacao: textoOpcional(TAMANHO_MAXIMO_CHAVE_NFSE),
});

export type DadosInformadosDaEmissao = z.infer<typeof dadosInformadosDaEmissaoSchema>;

/**
 * Corpo do `POST …/emissao`.
 *
 * `dadosInformados` é opcional **no contrato** e cobrado pelo motor: o Manual
 * o exige, uma API de emissão o recusa. O contrato não sabe qual motor a conta
 * usa — é a decisão 89 inteira —, então a regra não pode morar aqui.
 */
export const emitirNotaServicoSchema = z.object({
  dadosInformados: dadosInformadosDaEmissaoSchema.nullable().prefault(null),
});

export type EmitirNotaServico = z.infer<typeof emitirNotaServicoSchema>;

export const TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO = 255;

export const cancelarNotaServicoSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(5, 'Diga por que a nota está sendo cancelada.')
    .max(
      TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO,
      `O motivo tem no máximo ${TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO} caracteres.`,
    ),
});

export type CancelarNotaServico = z.infer<typeof cancelarNotaServicoSchema>;

/**
 * O que falta para a nota poder ser emitida, em texto para a pessoa.
 *
 * Lista, e não o primeiro erro: quem vai emitir precisa saber tudo o que
 * arrumar de uma vez, e não descobrir uma pendência por tentativa.
 */
export const pendenciasDeEmissaoSchema = z.object({
  motor: motorNfseSchema,
  /** O motor pede número e data digitados? É o que abre o diálogo na tela. */
  exigeDadosInformados: z.boolean(),
  pendencias: z.array(z.string()),
  /**
   * O que **não impede** e quem emite precisa ler antes de confirmar.
   *
   * Hoje é uma coisa só: a nota que passa do saldo a faturar numa conta que
   * alerta e permite (`.spec/015`, decisão 94). Lista à parte das pendências
   * porque a diferença é o botão — com pendência ele fica apagado, com aviso
   * ele funciona.
   */
  avisos: z.array(z.string()),
  /**
   * Os saldos a faturar que esta nota abate — a obra e o contrato dela.
   *
   * `null` quando a operação fiscal da nota **não gera financeiro**: ela não
   * fatura, e não há saldo a mostrar.
   */
  saldos: saldosAFaturarSchema.nullable(),
});

export type PendenciasDeEmissao = z.infer<typeof pendenciasDeEmissaoSchema>;

/* -------------------------------------------------------------------------- */
/* Leitura                                                                    */
/* -------------------------------------------------------------------------- */

export const notaServicoSchema = z.object({
  id: uuidSchema,
  /** Sequencial da conta. É o número pelo qual a nota é chamada antes de ser emitida. */
  numero: z.number().int(),
  situacao: situacaoNotaServicoSchema,

  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  clienteId: uuidSchema,
  clienteNome: z.string().nullable(),
  operacaoFiscalId: uuidSchema,
  operacaoFiscalNome: z.string().nullable(),
  /** Lido da operação **agora** — é o que diz à tela se a emissão vai gerar título. */
  operacaoGeraFinanceiro: z.boolean(),
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  obraId: uuidSchema.nullable(),
  obraCodigo: z.string().nullable(),
  obraNome: z.string().nullable(),
  contratoId: uuidSchema.nullable(),
  contratoNumero: z.string().nullable(),

  dataCompetencia: dataSchema,
  cidadePrestacaoId: uuidSchema.nullable(),
  cidadePrestacaoNome: z.string().nullable(),
  cidadePrestacaoUf: ufSchema.nullable(),

  descricaoServico: z.string(),
  codigoTributacaoNacional: z.string().nullable(),
  nbs: z.string().nullable(),
  codigoTributacaoMunicipal: z.string().nullable(),

  /** Copiados da operação fiscal na última gravação. */
  tributacaoIss: tributacaoIssSchema,
  issRetido: z.boolean(),

  valorServico: dinheiroSchema,
  valorDeducoes: dinheiroSchema,
  valorDescontoIncondicionado: dinheiroSchema,
  aliquotaIss: percentualSchema,
  valorBaseIss: dinheiroSchema,
  valorIss: dinheiroSchema,
  valorIssRetido: dinheiroSchema,
  valorIrrfRetido: dinheiroSchema,
  valorInssRetido: dinheiroSchema,
  valorCsllRetido: dinheiroSchema,
  valorPisRetido: dinheiroSchema,
  valorCofinsRetido: dinheiroSchema,
  valorLiquido: dinheiroSchema,

  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  condicaoPagamentoId: uuidSchema.nullable(),
  condicaoPagamentoDescricao: z.string().nullable(),

  informacoesComplementares: z.string().nullable(),
  observacao: z.string().nullable(),

  /*
    A autorização. Tudo nulo até a emissão — e o motor fica gravado na nota: a
    conta pode trocar de motor depois, e o cancelamento tem de ir para quem
    emitiu.
  */
  motor: motorNfseSchema.nullable(),
  numeroNfse: z.string().nullable(),
  chaveAcesso: z.string().nullable(),
  codigoVerificacao: z.string().nullable(),
  dataEmissao: dataSchema.nullable(),
  /** Identificador da nota no motor (o protocolo de uma API). Nulo no Manual. */
  idExterno: z.string().nullable(),
  /** O que a prefeitura apontou, quando rejeitou. */
  mensagemRejeicao: z.string().nullable(),

  canceladaEm: dataHoraSchema.nullable(),
  motivoCancelamento: z.string().nullable(),

  /**
   * Quantos títulos a receber a nota gerou. **Derivado**, nunca coluna — como
   * no contrato e na obra. Zero na nota de operação que não gera financeiro.
   */
  qtdTitulosGerados: z.number().int().min(0),

  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type NotaServico = z.infer<typeof notaServicoSchema>;

export const listaNotasServicoSchema = respostaListaSchema(notaServicoSchema);
export type ListaNotasServico = z.infer<typeof listaNotasServicoSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `q` casa com o **número da NFS-e** por prefixo; `numero` é o sequencial da
 * conta, exato. São dois porque são dois números: o do sistema é o que se diz
 * antes de emitir, e o da prefeitura é o que o cliente cita ao telefone.
 */
export const listarNotasServicoQuerySchema = paginacaoSchema.extend({
  numero: z.coerce.number().int().min(1).optional(),
  situacao: situacaoNotaServicoSchema.optional(),
  empresaId: uuidSchema.optional(),
  clienteId: uuidSchema.optional(),
  obraId: uuidSchema.optional(),
  contratoId: uuidSchema.optional(),
  /** Início do período da competência, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período da competência, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarNotasServicoQuery = z.infer<typeof listarNotasServicoQuerySchema>;

export const COLUNAS_ORDENAVEIS_NOTA_SERVICO = [
  'numero',
  'dataCompetencia',
  'dataEmissao',
  'createdAt',
] as const;
